import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, X, CalendarClock, Compass, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { syncNotifications, markNotificationsRead, clearNotifications } from "../lib/notificationsApi";

const ICONS = {
  saved_reminder: CalendarClock,
  weekend: Compass,
  monthly_checkin: Wrench,
};

// Simple V1 notifications: a bell with an unread badge that opens a plain list.
// Signed-in only. No preferences/categories — deliberately minimal.
export function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    if (!user) return;
    let savedIds = [];
    try {
      savedIds = JSON.parse(localStorage.getItem("roadrnr_saved") || "[]");
    } catch {
      savedIds = [];
    }
    const res = await syncNotifications(savedIds).catch(() => null);
    if (res) {
      setItems(res.notifications || []);
      setUnread(res.unread || 0);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const openPanel = async () => {
    setOpen(true);
    if (unread > 0) {
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      await markNotificationsRead().catch(() => {});
    }
  };

  const goRoute = (path) => {
    setOpen(false);
    navigate(path);
  };

  const clearAll = async () => {
    const prevItems = items;
    const prevUnread = unread;
    setItems([]);
    setUnread(0);
    try {
      await clearNotifications();
    } catch {
      setItems(prevItems);
      setUnread(prevUnread);
      toast.error("Could not clear notifications");
    }
  };

  if (!user) return null;

  return (
    <>
      <button
        data-testid="notifications-bell"
        onClick={openPanel}
        aria-label="Notifications"
        className="relative flex h-12 w-12 items-center justify-center bg-transparent transition-opacity duration-200 active:opacity-70"
      >
        <Bell className="h-6 w-6 text-white" strokeWidth={2.4} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread"
            className="absolute right-2.5 top-2.5 h-2.5 w-2.5 rounded-full bg-[#ff2a00] ring-2 ring-[#050505]"
          />
        )}
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[2000]" data-testid="notifications-panel">
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <div className="absolute inset-x-0 top-0 mx-auto flex max-h-[85vh] max-w-md flex-col overflow-hidden rounded-b-3xl border-b border-white/10 bg-[#0a0a0a] shadow-2xl shadow-black/70">
              <div className="flex items-center justify-between border-b border-white/10 px-5 pb-3 pt-[max(1rem,env(safe-area-inset-top))]">
                <h2 className="font-display text-lg font-bold text-white">Notifications</h2>
                <div className="flex items-center gap-2">
                  {items.length > 0 && (
                    <button
                      data-testid="notifications-clear-all"
                      onClick={clearAll}
                      className="rounded-full border border-white/10 bg-[#121212] px-3 py-1.5 text-xs font-semibold text-white/70 transition-colors hover:text-white active:scale-95"
                    >
                      Clear all
                    </button>
                  )}
                  <button
                    data-testid="notifications-close"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-[#121212] text-white active:scale-90"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
                {items.length === 0 ? (
                  <p
                    data-testid="notifications-empty"
                    className="py-10 text-center text-sm text-white/50"
                  >
                    You're all caught up.
                  </p>
                ) : (
                  items.map((n) => {
                    const Icon = ICONS[n.type] || Bell;
                    return (
                      <div
                        key={n.id}
                        data-testid="notification-item"
                        className="rounded-2xl border border-white/5 bg-[#121212] p-4"
                      >
                        <button
                          onClick={() => n.link && goRoute(n.link)}
                          disabled={!n.link}
                          className="flex w-full items-start gap-3 text-left disabled:cursor-default"
                        >
                          <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#ff2a00]" />
                          <span className="flex-1">
                            <span className="block text-sm font-semibold text-white">{n.title}</span>
                            <span className="mt-0.5 block text-sm text-white/60">{n.body}</span>
                          </span>
                          {!n.read && (
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#ff2a00]" />
                          )}
                        </button>

                        {n.type === "monthly_checkin" && n.meta && (
                          <div className="mt-3 flex gap-2 pl-8">
                            {n.meta.my_events && (
                              <button
                                data-testid="notif-my-events"
                                onClick={() => goRoute("/my-events")}
                                className="rounded-full bg-[#ff2a00] px-3 py-1.5 text-xs font-bold text-white active:scale-95"
                              >
                                My Events
                              </button>
                            )}
                            {n.meta.my_banner_ads && (
                              <button
                                data-testid="notif-my-banner-ads"
                                onClick={() => goRoute("/my-banner-ads")}
                                className="rounded-full border border-white/15 bg-[#1a1a1a] px-3 py-1.5 text-xs font-bold text-white active:scale-95"
                              >
                                My Banner Ads
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
